<template>
    <div class="page" :class="{'surface fill-height': phoneOnly}">
        <v-container :class="{'px-0': phoneOnly}">
            <v-card :flat="phoneOnly" class="px-1 px-lg-3" :class="{'transparent': phoneOnly, 'soft-shadow': pcOnly}">
                <v-card-title class="d-flex flex-wrap align-center" style="row-gap: 8px;">
                    <span class="me-2">Quarterly Due Report</span>
                    <v-chip v-if="currentBatch" small label outlined color="primary">Current batch: {{ currentBatch }}</v-chip>
                </v-card-title>
                <v-card-subtitle :class="{'pe-3': phoneOnly}">
                    Submit a quarterly progress report for each of your active students in the current batch.
                </v-card-subtitle>

                <v-card-text :class="{'px-0': phoneOnly}">
                    <!-- Deadline banner -->
                    <v-alert
                        v-if="!canSubmit"
                        type="warning"
                        outlined
                        prominent
                        dense
                        class="mb-4"
                        :class="{'mx-4': phoneOnly}"
                        icon="mdi-clock-alert-outline"
                    >
                        <div class="font-weight-medium">Submission deadline has passed</div>
                        <div class="caption mt-1">
                            Reports must be submitted by the {{ submissionDeadlineDay }}<sup>th</sup> of the month. Your pending students are still listed below for your reference. Please contact your supervisor if you need to submit a report for this period.
                        </div>
                    </v-alert>

                    <v-progress-circular v-if="loading && !pending" indeterminate color="primary" class="d-block mx-auto my-6" />

                    <v-list v-else-if="pending && pending.length" max-width="850px" class="pa-0 ma-0 transparent">
                        <template v-for="(item, i) in pending">
                            <v-list-item :key="rowKey(item)" class="my-1" :two-line="pcOnly" :three-line="phoneOnly">
                                <v-list-item-avatar tile :class="[item.reversed_report ? 'red darken-2' : 'primary', 'me-3 me-lg-6']" :size="phoneOnly ? 40 : 45">
                                    <v-icon color="white" style="opacity: 0.85" :small="phoneOnly">{{ item.reversed_report ? 'mdi-undo-variant' : 'mdi-account-school-outline' }}</v-icon>
                                </v-list-item-avatar>
                                <v-list-item-content class="py-2">
                                    <v-list-item-title class="font-weight-medium d-flex flex-wrap align-center" style="white-space: normal; gap: 6px;">
                                        <span>{{ item.user && item.user.name }}</span>
                                        <v-chip v-if="item.reversed_report" x-small label color="red darken-2" dark>{{ reversedCycleLabel(item) }}</v-chip>
                                    </v-list-item-title>
                                    <v-list-item-subtitle class="details-text" style="white-space: normal;">
                                        {{ item.course && item.course.name }}
                                        <span v-if="item.category" class="muted--text"> · {{ item.category }}</span>
                                    </v-list-item-subtitle>
                                    <div v-if="phoneOnly" class="mt-2">
                                        <v-btn small :color="actionColor(item)" depressed :disabled="!canActOn(item)" @click="openForm(item)">
                                            <v-icon left small>{{ actionIcon(item) }}</v-icon>
                                            {{ actionLabel(item) }}
                                        </v-btn>
                                    </div>
                                </v-list-item-content>
                                <v-list-item-action v-if="pcOnly">
                                    <v-btn small :color="actionColor(item)" depressed :disabled="!canActOn(item)" @click="openForm(item)">
                                        <v-icon left small>{{ actionIcon(item) }}</v-icon>
                                        {{ actionLabel(item) }}
                                    </v-btn>
                                </v-list-item-action>
                            </v-list-item>
                            <v-divider v-if="i < pending.length - 1" :inset="pcOnly" style="opacity: 0.6" :key="'div-' + rowKey(item)"></v-divider>
                        </template>
                    </v-list>

                    <div v-else-if="!loading" :class="{'px-4': phoneOnly}">
                        <v-alert type="info" color="blue-grey" text>
                            No students require a quarterly report this month. You're all caught up!
                        </v-alert>
                    </div>
                </v-card-text>
            </v-card>
        </v-container>

        <quarterly-report-form-dialog
            v-model="showFormDialog"
            :enrollment="activeEnrollment"
            @submitted="onSubmitted"
        />
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed';
import GlobalMethods from '@/helpers/global-methods';
import QuarterlyReportFormDialog from '@/components/quarterly-report/quarterly-report-form-dialog';

export default {
    name: 'QuarterlyReports',
    components: { QuarterlyReportFormDialog },
    data() {
        return {
            loading: false,
            showFormDialog: false,
            activeEnrollment: null,
        };
    },
    computed: {
        ...GlobalComputed,
        pending() { return this.$store.state.quarterlyReportsModule.pending; },
        currentBatch() { return this.$store.state.quarterlyReportsModule.currentBatch; },
        canSubmit() { return this.$store.state.quarterlyReportsModule.canSubmit; },
        submissionDeadlineDay() { return this.$store.state.quarterlyReportsModule.submissionDeadlineDay; },
    },
    methods: {
        ...GlobalMethods,
        loadData() {
            this.loading = true;
            this.$store.dispatch('loadQuarterlyPending')
                .catch(() => { /* handled silently; UI shows empty */ })
                .finally(() => { this.loading = false; });
        },
        rowKey(item) {
            return item.reversed_report ? `r-${item.reversed_report.id}` : `e-${item.enrollment_id}`;
        },
        reversedCycleLabel(item) {
            const cm = item.reversed_report && item.reversed_report.cycle_month;
            if (!cm) return 'Reversed — needs edit';
            const d = new Date(cm);
            if (Number.isNaN(d.getTime())) return 'Reversed — needs edit';
            const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
            return `Reversed — ${month} ${d.getUTCFullYear()}`;
        },
        // Reversed rows bypass the monthly deadline — the admin already accepted the rework.
        canActOn(item) {
            return !!item.reversed_report || this.canSubmit;
        },
        actionLabel(item) {
            if (item.reversed_report) return 'Edit & Resubmit';
            return this.canSubmit ? 'Add Report' : 'Locked';
        },
        actionIcon(item) {
            if (item.reversed_report) return 'mdi-pencil';
            return this.canSubmit ? 'mdi-plus' : 'mdi-lock-outline';
        },
        actionColor(item) {
            return item.reversed_report ? 'red darken-2' : 'primary';
        },
        openForm(item) {
            if (!this.canActOn(item)) {
                this.showMsg('The submission deadline has passed for this month.', 'error');
                return;
            }
            this.activeEnrollment = item;
            this.showFormDialog = true;
        },
        onSubmitted() {
            this.activeEnrollment = null;
            this.showMsg('Report submitted successfully.', 'success');
        },
    },
    created() {
        this.loadData();
    },
};
</script>
